import Axios from 'axios';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { defineMessages } from 'react-intl';

import { listItemMsg } from '@/shared/components/FormattedListValue';
import ToastrService from './ToastrService';

const messages = defineMessages({
  NoPermissionsForThisAction: 'You dont have permissions for this action',
  SomethingWentWrong: 'Something went wrong. Please try again later',
  TimeoutOccurred: 'Timeout occurred',
});

// the translation key might contain a parameter in the form of "Maximum length is {{p0}}"
// By default, the FormatMessage method expects a method with the name of the parameter as a property,
// this method creates an object with properties p0, p1, p2, based on the amount of parameters that were passed
const formatParameters = (intl, parameters, parametersEnumType) => {
  let formattedParameters = {
    ...parameters,
  };

  if (parameters && parameters.length) {
    for (let i = 0; i < parameters.length; i++) {
      const parameter = parameters[i];
      formattedParameters = {
        ...formattedParameters,
        [`p${i}`]: parametersEnumType // if the parameters are of a specific enum type, we will translate it accordingly.
          ? Array.isArray(parameter)
            ? parameter.map(x => intl.formatMessage(listItemMsg(parametersEnumType, x))).join(', ')
            : intl.formatMessage(listItemMsg(parametersEnumType, parameter))
          : parameter,
      };
    }
  }

  return formattedParameters;
};

export default intl => error => {
  if (!error) return Promise.reject();
  if (error.response && (error.response.status === 403 || error.response.status === 401)) {
    const unauthorizedMsg = intl.formatMessage(messages.NoPermissionsForThisAction);
    ToastrService.error(unauthorizedMsg);
    return Promise.reject();
  }
  // Ignore cancelled requests
  if (Axios.isCancel(error)) return;

  if (error.code === 'ECONNABORTED') {
    ApplicationInsights.trackException({ exception: error });
    const generalErrorMsg = intl.formatMessage(messages.TimeoutOccurred);
    ToastrService.error(generalErrorMsg);
    return Promise.reject(error.toString());
  }

  if (!error.code && error.response && error.response.data && !error.response.data.code) {
    // send exception to insights if its not a business exception
    if (error instanceof Error) {
      // error is a valid javascript Error, pass it to insights
      ApplicationInsights.trackException({ exception: error });
    } else {
      // Try to get message from toString
      let errorMessage = error.toString();

      if (errorMessage.indexOf('[object ') >= 0) errorMessage = null;
      ApplicationInsights.trackException(
        { exception: new Error(errorMessage || 'HttpError') },
        { originalError: JSON.stringify(error) },
      );
    }
  }
  if (error.response && error.response.data) {
    const {
      code = null,
      type = 'exceptioncode',
      parameters,
      parametersEnumType,
    } = error.response.data;

    const message = intl.formatMessage(
      {
        id: `${type}_${code}`.toLowerCase(),
        defaultMessage: intl.formatMessage(messages.SomethingWentWrong),
      },
      formatParameters(intl, parameters, parametersEnumType),
    );

    ToastrService.error(message);
    return Promise.reject(error.response.data);
  }

  if (error.code) {
    const type = error.type || 'exceptioncode';

    const message = intl.formatMessage(
      {
        id: `${type}_${error.code}`.toLowerCase(),
        defaultMessage: intl.formatMessage(messages.SomethingWentWrong),
      },
      formatParameters(intl, error.parameters, error.parametersEnumType),
    );
    ToastrService.error(message);
    return Promise.reject(error.code);
  }

  const generalErrorMsg = intl.formatMessage(messages.SomethingWentWrong);
  ToastrService.error(generalErrorMsg);
  return Promise.reject(error.toString());
};
